import React, { useMemo } from 'react';

import { PageProps } from '@fastlessjs/backoffice-layer/dist/components/pages/types';
import {
  Listing,
  ShowItem,
} from '@fastlessjs/backoffice-layer/dist/components/templates';
import { featureVoter } from '@fastlessjs/backoffice-layer/dist/services';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { Space } from 'antd';
import { pick } from 'lodash';
import { SuiviProduct } from 'products/types';

import EurekaRefreshButton from '../../components/EurekaRefreshButton';
import { t } from '../../config/translations';
import { suiviProductSchema } from '../../products/schemas';
import { ReturnStatusTag } from '../components/ReturnStatusTag';
import { useSuiviProducts, useSuiviReturn } from '../hooks';
import { traceReturnSchema } from '../schemas';
import { TraceReturn } from '../types';
import {
  formatProcessingStatusLabel,
  resolveProcessingStatus,
} from '../utils/traceStatusDisplay';

debug.registerComponent('ShowTraceReturn');

const ShowTraceReturn = ({ org, id }: PageProps) => {
  debug.trackRender('ShowTraceReturn');
  debug.log('ShowTraceReturn', `Rendering with org: ${org}, id: ${id}`);

  const { data, isLoading, isFetching } = useSuiviReturn({ tenantId: id });

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search.includes('debug=true')) {
      debug.enable();
      debug.log('ShowTraceReturn', 'Debug mode enabled from URL parameter');
    }
  }, []);

  debug.log('ShowTraceReturn', `Received data:`, data);
  debug.log('ShowTraceReturn', `Is loading: ${isLoading}, is fetching: ${isFetching}`);

  const processingStatusLabels = useMemo(
    () => ({
      pending: t('trace.processingStatus.pending'),
      unavailable: t('trace.processingStatus.unavailable'),
    }),
    [],
  );

  const displayData = useMemo((): TraceReturn | undefined => {
    if (!data) {
      return undefined;
    }
    const processingRaw = resolveProcessingStatus(data);
    return {
      ...data,
      processingStatus: formatProcessingStatusLabel(
        processingRaw,
        processingStatusLabels,
      ),
    };
  }, [data, processingStatusLabels]);

  const traceReturnSchemaDetail = featureVoter('detail:trace')
    ? pick(traceReturnSchema, [
        'properties.processingStatus',
        'properties.nsui',
        'properties.dlit',
        'properties.cal0002',
        'properties.cal0006',
        'properties.enterpriseId',
      ])
    : traceReturnSchema;

  const options = useMemo(() => {
    return {
      tenantId: data?.userId || '',
      suiviId: id,
    };
  }, [data?.userId, id]);

  const canRefreshFromEureka = !!data?.eurekaNsui;

  return (
    <>
      <ShowItem<TraceReturn>
        title={t('list.tracking')}
        org={org!}
        schema={traceReturnSchemaDetail}
        dataSource={displayData!}
        id={id}
        nameKey="suiviId"
        path="suivi"
        isFetching={isFetching}
        isLoading={isLoading}
        hasEdit={false}
        headerExtraActions={
          <Space wrap size="small">
            <ReturnStatusTag status={data?.returnStatus} />
            <EurekaRefreshButton
              entity="suivi"
              items={[{ suiviId: id }]}
              queryKeysToInvalidate={[['suivi', id]]}
              disabled={!canRefreshFromEureka}
            />
          </Space>
        }
      />

      <Listing<SuiviProduct>
        title={t('list.products')}
        idKey="productId"
        org={org!}
        schema={suiviProductSchema}
        options={options}
        rowKey={(record: SuiviProduct) => record?.productId || Math.random()}
        fetchHook={useSuiviProducts as any}
        hasView={false}
        hasNew={false}
        hasEdit={false}
        hasRefresh={false}
        itemUrl={(args) => ({ view: `suivi/${args?.record?.suiviId}` })}
      />
    </>
  );
};

debug.markInitialized('ShowTraceReturn');

export default ShowTraceReturn;
